import { useFormik } from 'formik';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Col, Input, InputRef, message, Row, Select } from 'antd';
import { FeedbackHttp } from '@/services';
import { FeedbackValidation } from '@/validations';
import { AppButton, AppModal, Flex } from '../common';
import { useUniversityContext, useUserContext } from '@/context';
import { useTranslation } from 'react-i18next';

const { Option } = Select;

const FeedbackModal = ({ open, hide, onComplete = undefined }) => {

  const [t] = useTranslation(['common', 'helpdesk']);

  const feedbackTypes = useMemo(() => [
    { label: t('helpdesk:labels.suggest-improvement'), trackerId: 21, categoryId: 70 },
    { label: t('helpdesk:labels.error-report'), trackerId: 14, categoryId: 68 },
    { label: t('helpdesk:labels.question'), trackerId: 13, categoryId: 69 },
    { label: t('helpdesk:labels.third-party-services'), trackerId: 15, categoryId: 71 },
    { label: t('helpdesk:labels.other'), trackerId: 9, categoryId: 72 },
  ],[t]);

  const inputRef = useRef<InputRef>();
  const [loading, setLoading] = useState(false);
  const { profile } = useUserContext();
  const { university } = useUniversityContext();

  const formik = useFormik({
    initialValues: {
      subject: undefined,
      description: undefined,
      type: undefined as any,
    },
    validationSchema: FeedbackValidation.create,
    onSubmit: values => {
      setLoading(true);
      FeedbackHttp.createFeedback({
        subject: values.subject,
        description: values.description,
        category: values.type.categoryId,
        tracker: values.type.trackerId,
      }, profile, university, t)
        .then(() => {
          if (onComplete) {
            onComplete();
          }
          handleHide();
          message.success({ content: t('common:messages.success-feedback') });
        })
        .finally(() => setLoading(false));
    },
  });

  const handleTypeChange = useCallback((value: any) => {
    const item = feedbackTypes.find(x => x.trackerId === Number(value));
    formik.setFieldValue('type', item);
  }, [formik]);

  const handleHide = () => {
    setLoading(false);
    formik.resetForm();
    formik.validateForm(null);
    hide();
  };

  const selectedTrackerId = formik.values.type?.trackerId;

  useEffect(() => {
    if (open && inputRef.current) {
      inputRef.current.focus();
    }
  }, [open]);

  return (
    <AppModal
      footer={null}
      destroyOnClose
      variant="nurture"
      open={open}
      onCancel={handleHide}
      title={t('common:labels.send-feedback')}
      style={{ maxWidth: '90%' }}
    >
      <Row gutter={[16, 16]}>
        <Col span={24}>
          <Input
            ref={inputRef}
            name="subject"
            placeholder={t('common:labels.title')}
            value={formik.values.subject}
            onChange={formik.handleChange}
          />
        </Col>
        <Col span={24}>
          <Select
            className="w-full"
            placeholder={t('common:labels.feedback-type')}
            onSelect={handleTypeChange}
            value={selectedTrackerId ? String(selectedTrackerId) : undefined}
          >
            {feedbackTypes.map(item =>
              <Option key={String(item.trackerId)}>{item.label}</Option>
            )}
          </Select>
        </Col>
        <Col span={24}>
          <Input.TextArea
            rows={5}
            name="description"
            onChange={formik.handleChange}
            value={formik.values.description}
            placeholder={t('common:labels.feedback-text')}
          />
        </Col>
        <Col span={24}>
          <Flex>
            <AppButton
              loading={loading}
              disabled={!formik.isValid}
              appVariant="nurture"
              onClick={formik.submitForm}
              className="!ms-auto">
              {t('common:labels.submit')}
            </AppButton>
          </Flex>
        </Col>
      </Row>
    </AppModal>
  );
};

export default FeedbackModal;
